import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { systemAPI } from '../services/api';
import {
  notifyBackendFailure,
  notifyBackendSuccess,
  subscribeBackendStatus,
} from '../utils/backendStatus';

const DOWN_POLL_MS = 15000;
const UP_POLL_MS = 60000;

const SystemStatusContext = createContext();

export const useSystemStatus = () => {
  const context = useContext(SystemStatusContext);
  if (!context) {
    throw new Error('useSystemStatus must be used within a SystemStatusProvider');
  }
  return context;
};

const isGatewayHealthy = (payload) => {
  const gateway = payload?.gateway;
  return gateway === 'healthy' || gateway === 'running';
};

export const SystemStatusProvider = ({ children }) => {
  const [isDown, setIsDown] = useState(false);
  const [checking, setChecking] = useState(false);
  const isDownRef = useRef(false);
  const inFlightRef = useRef(false);

  const checkHealth = useCallback(async () => {
    if (inFlightRef.current) {
      return !isDownRef.current;
    }
    inFlightRef.current = true;
    setChecking(true);
    try {
      const response = await systemAPI.healthCheck();
      if (isGatewayHealthy(response.data)) {
        notifyBackendSuccess();
        isDownRef.current = false;
        setIsDown(false);
        return true;
      }
      notifyBackendFailure({ immediate: true });
      isDownRef.current = true;
      setIsDown(true);
      return false;
    } catch (error) {
      notifyBackendFailure({ immediate: true });
      isDownRef.current = true;
      setIsDown(true);
      return false;
    } finally {
      inFlightRef.current = false;
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeBackendStatus(({ isDown: nextIsDown }) => {
      isDownRef.current = nextIsDown;
      setIsDown(nextIsDown);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timerId;

    const run = async () => {
      if (cancelled) {
        return;
      }
      await checkHealth();
      if (cancelled) {
        return;
      }
      const delay = isDownRef.current ? DOWN_POLL_MS : UP_POLL_MS;
      timerId = window.setTimeout(run, delay);
    };

    run();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkHealth();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkHealth]);

  const value = {
    isDown,
    checking,
    retry: checkHealth,
  };

  return (
    <SystemStatusContext.Provider value={value}>
      {children}
    </SystemStatusContext.Provider>
  );
};
